import React from 'react';

import { usePriceRowContext } from '../../../price-row-context';
import { useAppContext } from 'src/components/app-context';
import { Clickable } from 'src/components/clickable';

import { GoToStoreButton } from '../go-to-store-button';
import { StockPriceInfo } from '../stock-price-info';
import { StoreInfoColumn } from '../store-info-column';
import { OurChoiceBadge } from '../our-choice-badge';

import { getContainerProps } from '../../utils';

import {
  StyledEndColumn,
  StyledFeaturedRow,
  StyledFlexHorizontal,
  StyledFlexVertical,
  StyledMembershipBadgeWrapper,
  StyledMembershipText,
  StyledMembershipTextWrapper,
  StyledMembershipWrapper,
  StyledMiddleColumnDesktopPadding,
  StyledMiddleColumnMobilePadding,
  StyledProductName,
  StyledProductNameWrapper,
} from './styles';
import { Badge, BadgeSize } from '@prisjakt/ui/badge';
import { FontColor, FontEmphasis } from '@prisjakt/ui/text/variants';
import { IconInfoOutlined } from '@prisjakt/ui/icon/info-outlined';
import type { AlternativePrice } from 'src/types';
import type { ITableLinkProps } from '../../types';
import { ViewSize } from '../../types';

type Props = {
  alternativeOffer?: AlternativePrice;
  tableLinkProps: ITableLinkProps;
};

export const FeaturedColumns = (props: Props) => {
  const { t } = useAppContext();

  const { alternativeOffer, tableLinkProps } = props;

  const { offer, viewSize, isOurChoice, isInPriceList } = usePriceRowContext();

  const isAlternative = Boolean(alternativeOffer);
  const name = alternativeOffer?.name || offer.name;

  const hasMemberPrice = !!(alternativeOffer || offer)?.offerPrices
    ?.memberPrice;

  const externalUri = isAlternative
    ? alternativeOffer?.externalUri
    : offer.externalUri;

  const containerProps = getContainerProps(
    name || '',
    true,
    tableLinkProps.onClick,
    externalUri
  );

  const renderMembershipBadge = () => {
    if (!hasMemberPrice) {
      return null;
    }

    return (
      <StyledMembershipBadgeWrapper>
        <Badge
          color="YELLOW"
          size={BadgeSize.SMALL}
          text={t('product', 'promotions.members-price')}
        />
      </StyledMembershipBadgeWrapper>
    );
  };

  const renderMembershipInformation = () => {
    const isFree = !offer.membershipProgram?.requiresCompensation;
    if (!hasMemberPrice || isFree) return null;

    return (
      <StyledMembershipTextWrapper
        emphasis={FontEmphasis.HIGH}
        color={FontColor.DARK}
      >
        <IconInfoOutlined size={12} />
        <StyledMembershipText>
          {t('product', 'promotions.membership-price-info')}
        </StyledMembershipText>
      </StyledMembershipTextWrapper>
    );
  };

  return (
    <StyledFeaturedRow container={Clickable} containerProps={containerProps}>
      <StyledFlexHorizontal>
        <StyledMiddleColumnMobilePadding $viewSize={viewSize}>
          {viewSize === ViewSize.SMALL && renderMembershipInformation()}

          <StyledFlexVertical>
            {isOurChoice && isInPriceList && !alternativeOffer && (
              <OurChoiceBadge />
            )}

            <StyledFlexHorizontal>
              {!isAlternative && <StoreInfoColumn />}

              <StyledMiddleColumnDesktopPadding $viewSize={viewSize}>
                {viewSize >= ViewSize.MEDIUM && (
                  <StyledMembershipWrapper>
                    {renderMembershipInformation()}
                    {renderMembershipBadge()}
                  </StyledMembershipWrapper>
                )}

                <StyledFlexHorizontal>
                  <StyledProductNameWrapper
                    $alignStart={hasMemberPrice && !alternativeOffer}
                  >
                    {(viewSize >= ViewSize.MEDIUM || isAlternative) && (
                      <StyledProductName
                        $featured
                        $isAlternative={isAlternative}
                      >
                        {name}
                      </StyledProductName>
                    )}
                  </StyledProductNameWrapper>
                  <StyledFlexVertical>
                    {viewSize === ViewSize.SMALL && renderMembershipBadge()}

                    <StockPriceInfo alternativeOffer={alternativeOffer} />
                  </StyledFlexVertical>
                </StyledFlexHorizontal>
              </StyledMiddleColumnDesktopPadding>
            </StyledFlexHorizontal>
          </StyledFlexVertical>
        </StyledMiddleColumnMobilePadding>
        <StyledEndColumn $featured $viewSize={viewSize}>
          <GoToStoreButton />
        </StyledEndColumn>
      </StyledFlexHorizontal>
    </StyledFeaturedRow>
  );
};
